import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'temporal-polyfill/global';
import '@fontsource-variable/inter';
import './lib/dates';
import { applyReduceMotion } from './lib/motion';
import { applyTextSize } from './lib/textSize';
import { ensureAlbanianIntl } from './lib/intl-polyfill';
import { theme } from './app/theme';
import { pickersSq } from './i18n/mui-sq';
import { router } from './app/router';
import { AuthProvider } from './auth/AuthContext';
import { ApiError } from './api/client';

applyTextSize();
applyReduceMotion();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

// Albanian date/number data must be in place before the first render.
void ensureAlbanianIntl().then(() =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider theme={theme} defaultMode="system">
        <CssBaseline enableColorScheme />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sq" localeText={pickersSq}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StrictMode>,
  ),
);
